import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import React, { useState, useEffect, useRef } from 'react'
import './ApplyV2WorkExperienceStep.scss'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
    usePostWorkExperienceV2ApplyMutation,
    usePutWorkExperienceV2ApplyMutation,
    useDeleteWorkExperienceV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'

import { EducationIcon } from './icons/icon'
import { TrashIcon } from './icons/trash_icon'
import { PencilIcon } from './icons/pencil_icon'
import { LocationIcon } from './icons/location_icon'
import { CalendarIcon } from './icons/calendar_icon'
import { useTranslation } from 'react-i18next'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'

const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
const monthOptions = months.map((m) => ({ value: m, label: m }))

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 70 }, (_, i) => (currentYear - i).toString())
const yearOptions = years.map((y) => ({ value: y, label: y }))

type WorkExperience = {
    uid?: string
    title: string
    companyName: string
    location: string
    startMonth: string
    startYear: string
    endMonth: string
    endYear: string
    description: string
    current: boolean
}

const defaultWork: WorkExperience = {
    title: '',
    companyName: '',
    location: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    description: '',
    current: false,
}
const mapApiWorkExperience = (apiExp: any): WorkExperience => ({
    uid: apiExp.uid || '',
    title: apiExp.title || '',
    companyName: apiExp.companyName || '',
    location: apiExp.location || '',
    startMonth: apiExp.fromDate?.split('-')[1] || '',
    startYear: apiExp.fromDate?.split('-')[0] || '',
    endMonth: apiExp.toDate?.split('-')[1] || '',
    endYear: apiExp.toDate?.split('-')[0] || '',
    description: apiExp.description || '',
    current: apiExp.current || false,
})

const baseClass = 'WorkExperienceStep'

const WorkExperienceStep: React.FC = () => {
    const { t } = useTranslation()
    const { getTitle } = useApplyV2NavTranslations()
    const { postProfileData, onNextStep } = useApplyV2Context()
    const [postWork, { isLoading: isPostLoading }] = usePostWorkExperienceV2ApplyMutation()
    const [putWork, { isLoading: isPutLoading }] = usePutWorkExperienceV2ApplyMutation()
    const [deleteWork, { isLoading: isDeleteLoading }] = useDeleteWorkExperienceV2ApplyMutation()
    const [works, setWorks] = useState<WorkExperience[]>(() =>
        (postProfileData?.data?.workExperience || []).map(mapApiWorkExperience)
    )

    const [state, setState] = useState<'empty' | 'list' | 'add' | 'edit'>(
        (postProfileData?.data?.workExperience?.length ?? 0) === 0 ? 'empty' : 'list'
    )
    const [editUid, setEditUid] = useState<string | null>(null)
    const [form, setForm] = useState<WorkExperience>(defaultWork)
    const [noWork, setNoWork] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

    const uid = postProfileData?.data?.uid

    const descRef = useRef<HTMLTextAreaElement | null>(null)
    useEffect(() => {
        const el = descRef.current
        if (!el) return
        el.style.height = '39px'
        el.style.height = `${el.scrollHeight}px`
    }, [form.description])

    const workToApiBody = (data: WorkExperience) => ({
        ...(data.uid ? { uid: data.uid } : {}),
        title: data.title,
        companyName: data.companyName,
        location: data.location,
        fromDate: `${data.startYear}-${data.startMonth || '01'}-01`,
        toDate: `${data.endYear}-${data.endMonth || '01'}-01`,
        description: data.description,
        current: data.current,
    })

    const isFormValid =
        form.title.trim() !== '' &&
        form.companyName.trim() !== '' &&
        form.startMonth.trim() !== '' &&
        form.startYear.trim() !== '' &&
        form.endMonth.trim() !== '' &&
        form.endYear.trim() !== ''

    // Додати або оновити освіту
    const handleFormSubmit = async () => {
        if (!uid) return

        try {
            if (editUid) {
                // ОНОВЛЕННЯ (PUT)
                const res = await putWork({
                    uid,
                    body: { ...workToApiBody(form), uid: editUid },
                }).unwrap()

                setWorks((prev) => prev.map((ed) => (ed.uid === editUid ? { ...form, uid: editUid } : ed)))
            } else {
                // ДОДАВАННЯ (POST)
                const res = await postWork({
                    uid,
                    body: workToApiBody(form),
                }).unwrap()
                const newUid = res?.data?.workExperience?.uid || ''
                setWorks((prev) => [...prev, { ...form, uid: newUid }])
            }

            setState('list')
            setEditUid(null)
            setForm(defaultWork)
            setFieldErrors({})
        } catch (e: any) {
            const errFields: { [key: string]: string } = {}

            const errors = e?.data?.errors || []

            for (const err of errors) {
                const { field, msg } = err

                switch (field) {
                    case 'companyName':
                        errFields.companyName = msg || 'Required field'
                        break
                    case 'title':
                        errFields.title = msg || 'Required field'
                        break
                    case 'location':
                        errFields.location = msg || 'Required field'
                        break
                    case 'description':
                        errFields.description = msg || 'Required field'
                        break
                    case 'fromDate': {
                        const toDateError = errors.find((e: any) => e.field === 'toDate')
                        errFields.startYear = toDateError?.msg || msg || 'Invalid date'
                        break
                    }
                    case 'toDate':
                        errFields.endYear = msg || 'Invalid date'
                        break
                }
            }

            setFieldErrors(errFields)
        }
    }

    // Видалення
    const handleDeleteClick = async (idx: number) => {
        if (!uid) return
        const Work = works[idx]
        if (!Work.uid) return
        try {
            await deleteWork({ uid: uid, workUid: Work.uid }).unwrap()
            const updated = works.filter((_, i) => i !== idx)
            setWorks(updated)
            setState(updated.length ? 'list' : 'empty')
        } catch (e) {
            console.error('Failed to delete Work', e)
        }
    }

    // Додаємо новий
    const handleAddClick = () => {
        setForm(defaultWork)
        setEditUid(null)
        setState('add')
    }

    // Почати редагування
    const handleEditClick = (idx: number) => {
        const edu = works[idx]
        setForm(edu)
        setEditUid(edu.uid || null)
        setState('edit')
    }

    const handleFormChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = (
        e
    ) => {
        const { name, value } = e.currentTarget
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    // Відміна
    const handleCancel = () => {
        setState(works.length ? 'list' : 'empty')
        setEditUid(null)
        setForm(defaultWork)
    }

    useEffect(() => {
        setWorks((postProfileData?.data?.workExperience || []).map(mapApiWorkExperience))
        setState((postProfileData?.data?.workExperience?.length ?? 0) === 0 ? 'empty' : 'list')
    }, [postProfileData])

    // ---- UI ----
    return (
        <div className="WorkStep">
            {state === 'empty' && (
                <>
                    <div className="edu-empty">
                        <div className="edu-empty-icon">
                            <EducationIcon />
                        </div>
                        <div className="edu-empty-content">
                            <div className="edu-empty-title">
                                {noWork
                                    ? t('10302', "I don't have any work experience")
                                    : t('10301', 'No work experience added yet')}
                            </div>
                            <div className="edu-empty-actions">
                                <button
                                    className="add-edu-btn"
                                    type="button"
                                    disabled={noWork}
                                    onClick={handleAddClick}
                                >
                                    + {t('10300', 'Add experience') as string}
                                </button>
                                {/*<div className="no-edu-toggle" onClick={() => setNoWork((v) => !v)}>*/}
                                {/*    <div className={`toggle-switch${noWork ? ' checked' : ''}`}>*/}
                                {/*        <div className="toggle-thumb" />*/}
                                {/*    </div>*/}
                                {/*    <span>{t('10303', 'No experience') as string}</span>*/}
                                {/*</div>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={noWork}
                                    onChange={setNoWork}
                                    label={getTitle('10303', 'No experience')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row right">
                        <button type="button" disabled={!noWork} className="next-btn" onClick={onNextStep}>
                            {t('3035', 'Next')}
                        </button>
                    </div>
                </>
            )}

            {(state === 'add' || state === 'edit') && (
                <div className="edu-form">
                    <div className="edu-form-header">
                        <span>{state === 'edit' ? 'Edit experience' : 'Add experience'}</span>
                        <button className="close-btn" onClick={handleCancel}>
                            ×
                        </button>
                    </div>
                    <div className="">
                        <div className="field">
                            <label>{t('312', 'Company name') as string}*</label>
                            <input
                                className={fieldErrors.companyName ? 'has-error' : ''}
                                name="companyName"
                                value={form.companyName}
                                onChange={handleFormChange}
                                placeholder={t('6740', 'Your school, university, etc') as string}
                            />
                        </div>
                        {fieldErrors.companyName && <div className="field-error">{fieldErrors.companyName}</div>}
                    </div>
                    <div className="">
                        <div className="field">
                            <label>{t('4200', 'Role') as string}*</label>
                            <input
                                className={fieldErrors.title ? 'has-error' : ''}
                                name="title"
                                value={form.title}
                                onChange={handleFormChange}
                                placeholder={t('6737', 'Bachelor, Master, etc') as string}
                            />
                        </div>
                        {fieldErrors.title && <div className="field-error">{fieldErrors.title}</div>}
                    </div>
                    {/* <div className=" ">
            <div className="field">
              <label>Field of study</label>
              <input className={fieldErrors.institution ? 'has-error' : ''}
                name="fieldOfStudy" value={form.fieldOfStudy} onChange={handleFormChange} placeholder="E.g. Computer Science" />
            </div>
          </div> */}
                    <div className="">
                        <div className="field">
                            <label>{t('325', 'Start date') as string}*</label>
                            <div className="dob-input">
                                <ApplyV2Select
                                    name="startMonth"
                                    className="dob-select"
                                    placeholder={t('931', 'Month') as string}
                                    value={form.startMonth}
                                    options={monthOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'startMonth', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.startMonth}
                                />
                                <ApplyV2Select
                                    name="startYear"
                                    className="dob-select"
                                    placeholder={t('1322', 'Year') as string}
                                    value={form.startYear}
                                    options={yearOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'startYear', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.startYear}
                                />
                            </div>
                            {fieldErrors.startYear && <div className="field-error">{fieldErrors.startYear}</div>}
                        </div>
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('326', 'End date') as string}*</label>
                            <div className="dob-input">
                                <ApplyV2Select
                                    name="endMonth"
                                    className="dob-select"
                                    placeholder={t('931', 'Month') as string}
                                    value={form.endMonth}
                                    options={monthOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'endMonth', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.endMonth}
                                />

                                <ApplyV2Select
                                    name="endYear"
                                    className="dob-select"
                                    placeholder={t('1322', 'Year') as string}
                                    value={form.endYear}
                                    options={yearOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'endYear', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.endYear}
                                />
                            </div>
                            {fieldErrors.endYear && <div className="field-error">{fieldErrors.endYear}</div>}
                        </div>
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('1249', 'Location') as string}</label>
                            <input
                                className={fieldErrors.location ? 'has-error' : ''}
                                name="location"
                                value={form.location}
                                onChange={handleFormChange}
                                placeholder={t('1249', 'Location') as string}
                            />
                        </div>
                        {fieldErrors.location && <div className="field-error">{fieldErrors.location}</div>}
                    </div>

                    <div className="">
                        <div className="field full">
                            <label>{t('606', 'Description')}</label>
                            {/* <input
                className={fieldErrors.description ? 'has-error' : ''}
                name="description" value={form.description} onChange={handleFormChange}
                placeholder="Description" /> */}

                            <textarea
                                ref={descRef}
                                className={`auto-textarea${fieldErrors.description ? ' has-error' : ''}`}
                                name="description"
                                value={form.description}
                                onChange={handleFormChange}
                                placeholder={t('1650', 'Description') as string}
                                rows={1}
                                maxLength={5000}
                            />
                        </div>
                        {fieldErrors.description && <div className="field-error">{fieldErrors.description}</div>}
                    </div>
                    <div className="row actions">
                        <button type="button" className="cancel-btn" onClick={handleCancel}>
                            {t('416', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            className="add-btn"
                            onClick={handleFormSubmit}
                            disabled={!isFormValid || isPostLoading || isPutLoading}
                        >
                            {state === 'edit' ? t('12', 'Save') : t('179', 'Add')}
                        </button>
                    </div>
                </div>
            )}

            {state === 'list' && (
                <div>
                    <div className="edu-list edu-card">
                        {/*{works.map((edu, i) => (*/}
                        {/*    <div className="edu-block" key={edu.uid || i}>*/}
                        {/*        <div className="edu-card-content">*/}
                        {/*            <div className="edu-main">*/}
                        {/*                <div className="edu-title">{edu.companyName}</div>*/}
                        {/*                <div className="edu-degree">*/}
                        {/*                    {edu.title}*/}
                        {/*                    /!* {edu.fieldOfStudy && `• ${edu.fieldOfStudy}`} *!/*/}
                        {/*                </div>*/}
                        {/*                <div className="edu-date">*/}
                        {/*                    <CalendarIcon />*/}
                        {/*                    {edu.startMonth}.{edu.startYear} – {edu.endMonth}.{edu.endYear}*/}
                        {/*                </div>*/}
                        {/*                <div className="edu-location">*/}
                        {/*                    <LocationIcon />*/}
                        {/*                    {edu.location}*/}
                        {/*                </div>*/}
                        {/*                {edu.description && <div className="edu-description">{edu.description}</div>}*/}
                        {/*            </div>*/}
                        {/*            <div className="edu-actions">*/}
                        {/*                <button className="icon-btn" onClick={() => handleEditClick(i)} title="Edit">*/}
                        {/*                    <PencilIcon />*/}
                        {/*                </button>*/}
                        {/*                <button*/}
                        {/*                    className="icon-btn"*/}
                        {/*                    onClick={() => handleDeleteClick(i)}*/}
                        {/*                    title="Delete"*/}
                        {/*                    disabled={isDeleteLoading}*/}
                        {/*                >*/}
                        {/*                    <TrashIcon />*/}
                        {/*                </button>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*))}*/}
                        {works.map((edu, i) => (
                            <ApplyV2Card
                                key={edu.uid || i}
                                className="edu-block"
                                title={decodeHtmlEntitiesRecursive(edu.companyName)}
                                headerDescription={decodeHtmlEntitiesRecursive(edu.title)}
                                localeSection={edu.location}
                                date={{
                                    startYear: edu.startYear,
                                    endYear: edu.endYear,
                                    startMonth: edu.startMonth,
                                    endMonth: edu.endMonth,
                                }}
                                onEdit={() => handleEditClick(i)}
                                onDelete={() => handleDeleteClick(i)}
                            >
                                {edu.description}
                            </ApplyV2Card>
                        ))}

                        <div className="add-edu-row">
                            <button className="add-edu-btn" type="button" onClick={handleAddClick}>
                                + {t('10304', 'Add Work')}
                            </button>
                        </div>
                    </div>

                    <div className="row right">
                        <button type="button" className="next-btn" onClick={onNextStep}>
                            {t('3035', 'Next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default WorkExperienceStep
